/**
 * The cheque register: what it reads, and the four things it writes.
 *
 * Every read goes through `cheque_page` and `cheque_totals`, which are
 * `security invoker` — so `cheques_read` applies, and with it the clause that
 * says a cheque on an account somebody cannot open is not their cheque. There
 * is no `.filter()` in this file standing between one collector and another's
 * cheques, and deleting every line of it would not change that boundary.
 *
 * Filtering, the six tile figures and the money totals are all computed in the
 * database over the whole authorised population. A register that totalled the
 * loaded page and called it "total" would be the same mistake the book made
 * before Session 6.5.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    ChequeStatus, chequeState, type Cheque, type ChequeState,
} from '@timely/domain';
import { supabase } from '../lib/supabaseClient';

/* -------------------------------- the rows -------------------------------- */

export interface ChequeRow {
    id: string;
    customerId: string | null;
    customerName: string;
    chequeNumber: string;
    bankName: string;
    /** The day written on the cheque. Null when nobody has dated it yet. */
    chequeDate: string | null;
    amount: number;
    status: ChequeStatus;
    receivedDate: string | null;
    clearedDate: string | null;
    remarks: string;
    ownerMemberId: string | null;
    /** For the concurrency check; not shown anywhere. */
    updatedAt: string;
}

interface ChequeDbRow {
    id: string;
    customer_id: string | null;
    customer_name: string | null;
    cheque_number: string | null;
    bank_name: string | null;
    cheque_date: string | null;
    amount: number | string | null;
    status: string;
    received_date: string | null;
    cleared_date: string | null;
    remarks: string | null;
    owner_member_id: string | null;
    updated_at: string;
}

const toCheque = (row: ChequeDbRow): ChequeRow => ({
    id: row.id,
    customerId: row.customer_id,
    customerName: row.customer_name ?? '',
    chequeNumber: row.cheque_number ?? '',
    bankName: row.bank_name ?? '',
    chequeDate: row.cheque_date,
    amount: Number(row.amount) || 0,
    status: (row.status as ChequeStatus) ?? ChequeStatus.Pending,
    receivedDate: row.received_date,
    clearedDate: row.cleared_date,
    remarks: row.remarks ?? '',
    ownerMemberId: row.owner_member_id,
    updatedAt: row.updated_at,
});

/**
 * Where a row stands, for the badge and for which actions it carries.
 *
 * Derived here from the same rule the database filtered by, so a row's badge
 * and the tile that counted it cannot disagree. `today` comes from the
 * caller, which is the company's day rather than the browser's.
 */
export const stateOf = (row: ChequeRow, today: Date): ChequeState =>
    chequeState(
        { status: row.status, chequeDate: row.chequeDate ? new Date(row.chequeDate) : new Date(NaN) },
        today,
    );

/* ------------------------------ the question ------------------------------ */

export interface ChequeFilters {
    state: ChequeState | 'any';
    search: string;
    owner: string;
    bank: string;
    /** Calendar days, not timestamps: a cheque is dated, not timed. */
    from: string;
    to: string;
}

export const DEFAULT_CHEQUE_FILTERS: ChequeFilters = {
    state: 'any', search: '', owner: 'any', bank: 'any', from: '', to: '',
};

export const CHEQUE_PAGE = 50;

export interface ChequeTotals {
    matchedCount: number;
    matchedAmount: number;
    /** Every cheque this person can see, whatever the filters. */
    visibleCount: number;
    byState: Record<ChequeState, { count: number; amount: number }>;
}

const EMPTY_STATE = { count: 0, amount: 0 };

const readTotals = (row: Record<string, unknown> | null | undefined): ChequeTotals | null => {
    if (!row) return null;
    const figure = (count: unknown, amount: unknown) => ({
        count: Number(count) || 0, amount: Number(amount) || 0,
    });
    return {
        matchedCount: Number(row['matched_count']) || 0,
        matchedAmount: Number(row['matched_amount']) || 0,
        visibleCount: Number(row['visible_count']) || 0,
        byState: {
            due: figure(row['due_count'], row['due_amount']),
            overdue: figure(row['overdue_count'], row['overdue_amount']),
            upcoming: figure(row['upcoming_count'], row['upcoming_amount']),
            hold: figure(row['hold_count'], row['hold_amount']),
            bounced: figure(row['bounced_count'], row['bounced_amount']),
            cleared: figure(row['cleared_count'], row['cleared_amount']),
        },
    };
};

export const emptyChequeTotals = (): ChequeTotals => ({
    matchedCount: 0, matchedAmount: 0, visibleCount: 0,
    byState: {
        due: EMPTY_STATE, overdue: EMPTY_STATE, upcoming: EMPTY_STATE,
        hold: EMPTY_STATE, bounced: EMPTY_STATE, cleared: EMPTY_STATE,
    },
});

const filterArgs = (workspaceId: string, filters: ChequeFilters) => ({
    p_workspace: workspaceId,
    p_state: filters.state,
    p_search: filters.search,
    p_owner: filters.owner,
    p_bank: filters.bank,
    p_from: filters.from || null,
    p_to: filters.to || null,
});

export interface BankOption { bankName: string; chequeCount: number }

export function useChequeRegister(workspaceId: string, filters: ChequeFilters) {
    const [rows, setRows] = useState<ChequeRow[]>([]);
    const [totals, setTotals] = useState<ChequeTotals | null>(null);
    const [banks, setBanks] = useState<BankOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const key = JSON.stringify([
        workspaceId, filters.state, filters.search, filters.owner,
        filters.bank, filters.from, filters.to,
    ]);
    const issuedFor = useRef(key);

    const load = useCallback(async (from = 0) => {
        issuedFor.current = key;
        if (from === 0) setLoading(true); else setLoadingMore(true);
        setError(null);

        const client = supabase();
        const args = filterArgs(workspaceId, filters);

        const [pageResult, totalsResult, banksResult] = await Promise.all([
            client.rpc('cheque_page', { ...args, p_limit: CHEQUE_PAGE, p_offset: from }),
            // Only with the first page: the totals cannot change between one
            // page of a question and the next.
            from === 0 ? client.rpc('cheque_totals', args) : Promise.resolve({ data: null, error: null }),
            from === 0 ? client.rpc('cheque_banks', { p_workspace: workspaceId })
                : Promise.resolve({ data: null, error: null }),
        ]);

        // An answer that arrives after the filters moved on describes a
        // question nobody is asking any more.
        if (issuedFor.current !== key) return;

        if (pageResult.error) {
            setError('We could not load your cheques. Try again in a moment.');
            setLoading(false);
            setLoadingMore(false);
            return;
        }

        const page = ((pageResult.data ?? []) as unknown as ChequeDbRow[]).map(toCheque);
        setRows(previous => (from === 0 ? page : [...previous, ...page]));

        if (from === 0) {
            const row = (Array.isArray(totalsResult.data) ? totalsResult.data[0] : totalsResult.data) as
                Record<string, unknown> | null;
            setTotals(totalsResult.error ? null : readTotals(row));
            setBanks(((banksResult.data ?? []) as { bank_name: string; cheque_count: number }[])
                .map(b => ({ bankName: b.bank_name, chequeCount: Number(b.cheque_count) || 0 })));
        }

        setLoading(false);
        setLoadingMore(false);
        // `filters` is covered by `key`; depending on the object would reload
        // on every render because the screen rebuilds it from the address bar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    useEffect(() => { void load(0); }, [load]);

    const hasMore = totals !== null
        ? rows.length < totals.matchedCount
        : rows.length > 0 && rows.length % CHEQUE_PAGE === 0;

    return {
        rows, totals, banks, loading, loadingMore, error, hasMore,
        reload: () => load(0),
        loadMore: () => load(rows.length),
    };
}

/* ------------------------------- the writes ------------------------------- */

/** What a save failed at, in words a person can act on. */
export class ChequeError extends Error {
    constructor(message: string, readonly kind: 'conflict' | 'denied' | 'gone' | 'rule' | 'unknown') {
        super(message);
    }
}

const readFailure = (message: string | undefined): ChequeError => {
    const text = message ?? '';
    if (/changed while you were working/i.test(text)) {
        return new ChequeError(
            'Somebody else changed this cheque while you had it open. Look at what it says now, '
            + 'then decide what to do.',
            'conflict',
        );
    }
    if (/cannot be marked/i.test(text)) {
        return new ChequeError(
            'That is not a move this cheque can make any more — it has moved on since this screen loaded.',
            'rule',
        );
    }
    if (/not available/i.test(text)) {
        return new ChequeError('That cheque is not available.', 'gone');
    }
    if (/row-level security|permission denied|cannot change cheques|insufficient/i.test(text)) {
        return new ChequeError('You do not have permission to change cheques for this company.', 'denied');
    }
    return new ChequeError('That did not save. Try again in a moment.', 'unknown');
};

/**
 * Move a cheque from one state to another.
 *
 * Through `change_cheque_status`, which does the two writes — the cheque and
 * the line in the customer's history — in one transaction, re-checks the
 * capability and the account's visibility itself, and refuses a stale edit
 * with a prompt 409 rather than silently overwriting somebody else's decision.
 */
export async function changeChequeStatus(
    workspaceId: string,
    chequeId: string,
    status: ChequeStatus,
    expectedUpdatedAt: string | null,
    note = '',
): Promise<void> {
    const { error } = await supabase().rpc('change_cheque_status', {
        p_workspace: workspaceId,
        p_cheque: chequeId,
        p_status: status,
        p_expected_updated_at: expectedUpdatedAt,
        p_note: note,
    });
    if (error) throw readFailure(error.message);
}

export interface ChequeDraft {
    /** Generated before the first attempt, so a retry cannot create a second. */
    id: string;
    customerId: string | null;
    customerName: string;
    chequeNumber: string;
    bankName: string;
    chequeDate: string;
    amount: string;
    status: ChequeStatus;
    receivedDate: string;
    remarks: string;
    ownerMemberId: string | null;
}

export const newChequeId = (): string =>
    (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/**
 * Record or correct a cheque.
 *
 * **The id is generated before the first attempt and reused on every retry.**
 * A save that times out and is pressed again must not produce a second cheque
 * for the same money — `upsert` on the primary key makes the retry land on the
 * row the first attempt may already have written.
 *
 * A plain table write, deliberately: the policies already say what may be
 * written, by whom, and onto which accounts. A privileged route here would be
 * a service key doing a job Row Level Security already does correctly.
 */
export async function saveCheque(
    workspaceId: string,
    draft: ChequeDraft,
    memberId: string | null,
): Promise<void> {
    const amount = Math.abs(Number(draft.amount.replace(/[^0-9.-]/g, '')) || 0);

    const { error } = await supabase().from('pdc_cheques').upsert({
        id: draft.id,
        workspace_id: workspaceId,
        customer_id: draft.customerId,
        customer_name: draft.customerName.trim(),
        cheque_number: draft.chequeNumber.trim(),
        bank_name: draft.bankName.trim(),
        // A date input gives a calendar day. Midday keeps a timezone from
        // shifting it onto the day before, which is how a cheque dated the 1st
        // showed as due on the 31st.
        cheque_date: draft.chequeDate ? new Date(`${draft.chequeDate}T12:00:00`).toISOString() : null,
        amount,
        status: draft.status,
        received_date: draft.receivedDate
            ? new Date(`${draft.receivedDate}T12:00:00`).toISOString()
            : null,
        remarks: draft.remarks.trim(),
        owner_member_id: draft.ownerMemberId,
        added_by_member_id: memberId,
        updated_at: new Date().toISOString(),
    }, { onConflict: 'id' });

    if (error) throw readFailure(error.message);
}

export async function deleteCheque(workspaceId: string, chequeId: string): Promise<void> {
    const { error } = await supabase()
        .from('pdc_cheques').delete()
        .eq('id', chequeId).eq('workspace_id', workspaceId);
    if (error) throw readFailure(error.message);
}

/* -------------------------- linking to a customer ------------------------- */

export interface CustomerOption {
    id: string;
    company: string;
    sourceKey: string | null;
    total: number;
}

/**
 * The accounts somebody may attach a cheque to.
 *
 * Deliberately `book_page` rather than a new endpoint: it already searches the
 * caller's own book under `can_see_customer()`, so an account outside their
 * scope cannot appear here, cannot be found by typing its exact name, and
 * cannot be linked by supplying its id — the insert policy asks the same
 * question again.
 *
 * A four-thousand-option select was the alternative, and it is unusable on a
 * phone and slow everywhere.
 */
export async function searchCustomers(workspaceId: string, query: string): Promise<CustomerOption[]> {
    const { data, error } = await supabase().rpc('book_page', {
        p_workspace: workspaceId,
        p_search: query,
        p_settlement: 'all',
        p_sort: 'name',
        p_limit: 20,
        p_offset: 0,
    });
    if (error) return [];
    return ((data ?? []) as { id: string; company: string; source_key: string | null; total: number }[])
        .map(c => ({
            id: c.id,
            company: c.company,
            sourceKey: c.source_key,
            total: Number(c.total) || 0,
        }));
}

/** One account by id, for a cheque that already names one. */
export async function customerById(workspaceId: string, customerId: string): Promise<CustomerOption | null> {
    const { data } = await supabase()
        .from('customers').select('id, company, source_key, total')
        .eq('workspace_id', workspaceId).eq('id', customerId).maybeSingle();
    if (!data) return null;
    const row = data as { id: string; company: string; source_key: string | null; total: number };
    return { id: row.id, company: row.company, sourceKey: row.source_key, total: Number(row.total) || 0 };
}

export type { Cheque };
